import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Clock, X, RotateCcw } from "lucide-react";
import type { PageForEdit } from "../pages/types";
import { GET_PAGE_VERSIONS } from "../pages/mutations";

// ── Date utilities ──────────────────────────────────────────────────

/** Group label for a version's date (Today, Yesterday, or a date). */
function dayLabel(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  if (date >= todayStart) return "Today";

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  if (date >= yesterdayStart) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
}

// ── Types ───────────────────────────────────────────────────────────

interface PageVersion {
  id: string;
  pageId: string;
  version: number;
  content: string; // JSON string
  createdBy: string | null;
  createdByEmail: string | null;
  createdAt: string;
}

interface VersionHistoryProps {
  page: PageForEdit;
  open: boolean;
  selectedVersionId: string | null;
  onSelectVersion: (version: PageVersion) => void;
  onRestore: (version: PageVersion) => Promise<void>;
  onClose: () => void;
}

// ── Component ───────────────────────────────────────────────────────

export type { PageVersion };

export function VersionHistory({
  page,
  open,
  selectedVersionId,
  onSelectVersion,
  onRestore,
  onClose,
}: VersionHistoryProps) {
  // Fetch all versions for this page
  const {
    data: versionsData,
    loading: loadingVersions,
    error: versionsError,
  } = useQuery<{
    allPageVersions: PageVersion[];
  }>(GET_PAGE_VERSIONS, {
    variables: {
      filter: { pageId: { eq: page.id } },
      page: 0,
      perPage: 200,
    },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const versions = versionsData?.allPageVersions ?? [];

  // Author display name from the server-resolved email (part before @).
  // Tenant portal users cannot list users, so the version carries its author.
  const authorDisplay = (v: PageVersion): string => {
    if (!v.createdByEmail) return "Unknown";
    const atIdx = v.createdByEmail.indexOf("@");
    return atIdx > 0 ? v.createdByEmail.slice(0, atIdx) : v.createdByEmail;
  };

  // Identify the published version by matching content
  const publishedVersionId = useMemo(() => {
    if (!page.publishedContent) return null;
    for (const v of versions) {
      if (v.content === page.publishedContent) return v.id;
    }
    return null;
  }, [versions, page.publishedContent]);

  // The latest version is the current draft baseline
  const latestVersionId = versions.length > 0 ? versions[0].id : null;

  // Group versions by day
  const groupedVersions = useMemo(() => {
    const groups: Array<{ label: string; versions: PageVersion[] }> = [];
    let currentLabel = "";
    let currentGroup: PageVersion[] = [];

    for (const v of versions) {
      const label = dayLabel(v.createdAt);
      if (label !== currentLabel) {
        if (currentGroup.length > 0) {
          groups.push({ label: currentLabel, versions: currentGroup });
        }
        currentLabel = label;
        currentGroup = [v];
      } else {
        currentGroup.push(v);
      }
    }
    if (currentGroup.length > 0) {
      groups.push({ label: currentLabel, versions: currentGroup });
    }
    return groups;
  }, [versions]);

  const isCurrentDraft = selectedVersionId === latestVersionId;
  const selectedVersion = versions.find((v) => v.id === selectedVersionId);

  return (
    <aside
      className="flex-none overflow-hidden bg-paper-hi transition-[width] duration-[220ms] ease-[cubic-bezier(0.3,0.9,0.3,1)]"
      style={{
        width: open ? 340 : 0,
        borderLeft: open
          ? "1px solid var(--color-ink-6)"
          : "1px solid transparent",
      }}
    >
      <div className="w-[340px] h-full flex flex-col">
        {/* Header */}
        <div className="px-4 pt-[14px] pb-[10px] border-b border-paper-2">
          <div className="flex items-center justify-between">
            <h2 className="text-[14px] font-semibold text-ink">
              Version history
            </h2>
            <button
              onClick={onClose}
              title="Close"
              className="text-ink-5 p-1 rounded-[7px] hover:bg-paper-2 transition-colors"
            >
              <X size={16} strokeWidth={2} />
            </button>
          </div>
          <p className="text-[12px] text-ink-4 mt-[3px]">
            A version is saved every time you save the draft.
          </p>
        </div>

        {/* Version list */}
        {loadingVersions && versions.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-[13px] text-ink-4">
            Loading versions...
          </div>
        ) : versionsError && versions.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-center px-[30px] text-[12.5px] text-rust">
            Couldn't load version history. Close the panel and try again.
          </div>
        ) : versions.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex-1 overflow-y-auto px-2 py-2">
            {groupedVersions.map((group) => (
              <div key={group.label}>
                <div className="text-[10.5px] font-bold tracking-[0.06em] uppercase text-ink-5 px-2 pt-[10px] pb-1">
                  {group.label}
                </div>
                {group.versions.map((v) => (
                  <VersionRow
                    key={v.id}
                    version={v}
                    isSelected={v.id === selectedVersionId}
                    isPublished={v.id === publishedVersionId}
                    isLatest={v.id === latestVersionId}
                    authorName={authorDisplay(v)}
                    onClick={() => onSelectVersion(v)}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {versions.length > 0 && (
          <div className="border-t border-paper-2 px-4 py-3 flex flex-col gap-2">
            <p className="text-[11.5px] text-ink-4 leading-snug">
              Restoring replaces your <b className="font-semibold">draft</b>{" "}
              with this version. Your live site is not changed until you publish.
            </p>
            <button
              disabled={isCurrentDraft || !selectedVersion}
              onClick={() => {
                if (selectedVersion) {
                  void onRestore(selectedVersion);
                }
              }}
              className="w-full text-[13px] font-semibold rounded-[10px] px-3.5 py-2.5 border border-tide bg-tide text-white hover:bg-tide-d disabled:opacity-45 disabled:cursor-default transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw size={14} strokeWidth={2} />
              {isCurrentDraft
                ? "This is your current draft"
                : "Restore this version to draft"}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-[30px] text-ink-4">
      <Clock size={30} strokeWidth={1.5} className="mb-[10px] opacity-60" />
      <h3 className="text-[13.5px] font-semibold text-ink-2 mb-[5px]">
        No versions yet
      </h3>
      <p className="text-[12px] max-w-[230px]">
        Save your draft and each save will appear here, so you can always go
        back.
      </p>
    </div>
  );
}

function VersionRow({
  version,
  isSelected,
  isPublished,
  isLatest,
  authorName,
  onClick,
}: {
  version: PageVersion;
  isSelected: boolean;
  isPublished: boolean;
  isLatest: boolean;
  authorName: string;
  onClick: () => void;
}) {
  const timeStr = new Date(version.createdAt).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-[9px] px-[11px] py-[10px] cursor-pointer border transition-colors ${
        isSelected
          ? "bg-tide-ss border-tide-s"
          : "bg-transparent border-transparent hover:bg-paper-2"
      }`}
    >
      <div className="flex items-center gap-[7px]">
        <span className="font-semibold text-[13px] text-ink-2">{timeStr}</span>
        {isPublished && (
          <span className="text-[10px] font-bold px-[7px] py-[1px] rounded-full bg-sage-s text-sage">
            LIVE
          </span>
        )}
        {isLatest && (
          <span className="text-[10px] font-bold px-[7px] py-[1px] rounded-full bg-spark-s text-[#6b5426]">
            CURRENT DRAFT
          </span>
        )}
      </div>
      <div className="text-[11.5px] text-ink-4 mt-[3px]">{authorName}</div>
      <div className="text-[11.5px] text-ink-5 mt-[2px]">
        Version {version.version}
      </div>
    </button>
  );
}
